import React, { useEffect, useState } from 'react'

const pulseAnimation = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = pulseAnimation
  document.head.appendChild(styleSheet)
}

interface NearbyHighlight {
  id: number
  name: string
  icon_url: string
  display_order: number
}

interface ViewType {
  id: number
  name: string
  icon_url: string
  display_order: number
}

interface Amenity {
  id: number
  name: string
  icon_url: string
  display_order: number
}

interface BuildingFacility {
  id: number
  name: string
  icon_url: string
  display_order: number
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  listingTitle: string
  shortDescription: string
  uniqueSellingPoints: string[]
  nearbyHighlights: string[]
  noiseLevel: string
  viewType: string[]
  amenities: string[]
  buildingFacilities: string[]
}

interface Step1_2Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
  isSubmittingDraft?: boolean
}

const Step1_2: React.FC<Step1_2Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
  isSubmittingDraft = false,
}) => {
  const [progress, setProgress] = useState(0)
  const [nearbyHighlights, setNearbyHighlights] = useState<NearbyHighlight[]>([])
  const [highlightsLoading, setHighlightsLoading] = useState(true)
  const [highlightsError, setHighlightsError] = useState<string | null>(null)
  const [viewTypes, setViewTypes] = useState<ViewType[]>([])
  const [viewTypesLoading, setViewTypesLoading] = useState(true)
  const [viewTypesError, setViewTypesError] = useState<string | null>(null)
  const [amenities, setAmenities] = useState<Amenity[]>([])
  const [amenitiesLoading, setAmenitiesLoading] = useState(true)
  const [amenitiesError, setAmenitiesError] = useState<string | null>(null)
  const [buildingFacilities, setBuildingFacilities] = useState<BuildingFacility[]>([])
  const [buildingFacilitiesLoading, setBuildingFacilitiesLoading] = useState(true)
  const [buildingFacilitiesError, setBuildingFacilitiesError] = useState<string | null>(null)

  const uniqueSellingPointOptions = ['Spacious', 'Quiet', 'Central', 'Modern', 'Unique', 'Cozy']

  useEffect(() => {
    const fetchNearbyHighlights = async () => {
      try {
        setHighlightsLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/features/NEARBY_HIGHLIGHT`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          setNearbyHighlights(
            data.data.sort(
              (a: NearbyHighlight, b: NearbyHighlight) => a.display_order - b.display_order,
            ),
          )
        } else {
          throw new Error(data.message || 'Failed to fetch nearby highlights')
        }
      } catch (err: any) {
        setHighlightsError(err.message || 'An error occurred')
      } finally {
        setHighlightsLoading(false)
      }
    }

    fetchNearbyHighlights()
  }, [])

  const noiseLevelOptions = ['Quiet', 'Moderate', 'Busy/active area']

  useEffect(() => {
    const fetchViewTypes = async () => {
      try {
        setViewTypesLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/features/VIEW_TYPE`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          setViewTypes(
            data.data.sort((a: ViewType, b: ViewType) => a.display_order - b.display_order),
          )
        } else {
          throw new Error(data.message || 'Failed to fetch view types')
        }
      } catch (err: any) {
        setViewTypesError(err.message || 'An error occurred')
      } finally {
        setViewTypesLoading(false)
      }
    }

    fetchViewTypes()
  }, [])

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        setAmenitiesLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/features/AMENITY`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          setAmenities(
            data.data.sort((a: Amenity, b: Amenity) => a.display_order - b.display_order),
          )
        } else {
          throw new Error(data.message || 'Failed to fetch amenities')
        }
      } catch (err: any) {
        setAmenitiesError(err.message || 'An error occurred')
      } finally {
        setAmenitiesLoading(false)
      }
    }

    fetchAmenities()
  }, [])

  useEffect(() => {
    const fetchBuildingFacilities = async () => {
      try {
        setBuildingFacilitiesLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/features/BUILDING_FACILITY`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          setBuildingFacilities(
            data.data.sort(
              (a: BuildingFacility, b: BuildingFacility) => a.display_order - b.display_order,
            ),
          )
        } else {
          throw new Error(data.message || 'Failed to fetch building facilities')
        }
      } catch (err: any) {
        setBuildingFacilitiesError(err.message || 'An error occurred')
      } finally {
        setBuildingFacilitiesLoading(false)
      }
    }

    fetchBuildingFacilities()
  }, [])

  const calculateProgress = () => {
    const fields = [
      formData.listingTitle,
      formData.shortDescription,
      formData.uniqueSellingPoints && formData.uniqueSellingPoints.length > 0,
      formData.nearbyHighlights && formData.nearbyHighlights.length > 0,
      formData.noiseLevel,
      formData.viewType && formData.viewType.length > 0,
      formData.amenities && formData.amenities.length > 0,
      formData.buildingFacilities && formData.buildingFacilities.length > 0,
    ]

    const filledFields = fields.filter((field) => field && field !== '').length
    const progressPercentage = Math.round((filledFields / fields.length) * 100)
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [
    formData.listingTitle,
    formData.shortDescription,
    formData.uniqueSellingPoints,
    formData.nearbyHighlights,
    formData.noiseLevel,
    formData.viewType,
    formData.amenities,
    formData.buildingFacilities,
    onStepProgress,
  ])

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value })
  }

  const handleUniqueSellingPointChange = (point: string, checked: boolean) => {
    const currentPoints = formData.uniqueSellingPoints || []
    let updatedPoints: string[]

    if (checked) {
      updatedPoints = [...currentPoints, point]
    } else {
      updatedPoints = currentPoints.filter((p) => p !== point)
    }

    updateFormData({ uniqueSellingPoints: updatedPoints })
  }

  const handleNearbyHighlightChange = (highlight: string, checked: boolean) => {
    const currentHighlights = formData.nearbyHighlights || []
    let updatedHighlights: string[]

    if (checked) {
      updatedHighlights = [...currentHighlights, highlight]
    } else {
      updatedHighlights = currentHighlights.filter((h) => h !== highlight)
    }

    updateFormData({ nearbyHighlights: updatedHighlights })
  }

  const handleViewTypeChange = (viewType: string, checked: boolean) => {
    const currentViewTypes = formData.viewType || []
    let updatedViewTypes: string[]

    if (checked) {
      updatedViewTypes = [...currentViewTypes, viewType]
    } else {
      updatedViewTypes = currentViewTypes.filter((v) => v !== viewType)
    }

    updateFormData({ viewType: updatedViewTypes })
  }

  const handleAmenityChange = (amenity: string, checked: boolean) => {
    const currentAmenities = formData.amenities || []
    let updatedAmenities: string[]

    if (checked) {
      updatedAmenities = [...currentAmenities, amenity]
    } else {
      updatedAmenities = currentAmenities.filter((a) => a !== amenity)
    }

    updateFormData({ amenities: updatedAmenities })
  }

  const handleBuildingFacilityChange = (facility: string, checked: boolean) => {
    const currentFacilities = formData.buildingFacilities || []
    let updatedFacilities: string[]

    if (checked) {
      updatedFacilities = [...currentFacilities, facility]
    } else {
      updatedFacilities = currentFacilities.filter((f) => f !== facility)
    }

    updateFormData({ buildingFacilities: updatedFacilities })
  }

  const isNextDisabled = progress < 100 || isSubmittingDraft

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Listing title
          </label>
          <input
            type="text"
            placeholder="Enter the title of the listing"
            value={formData.listingTitle || ''}
            onChange={(e) => handleInputChange('listingTitle', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Short description
          </label>
          <div style={{ position: 'relative' }}>
            <textarea
              placeholder="Enter the short description"
              value={formData.shortDescription || ''}
              onChange={(e) => handleInputChange('shortDescription', e.target.value)}
              maxLength={50}
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                boxSizing: 'border-box',
                resize: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                fontSize: '12px',
                color: '#999',
              }}
            >
              {(formData.shortDescription || '').length}/50 characters
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Unique selling points
          </label>
          <p
            style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: '1.4',
            }}
          >
            Pick a few words that best describe your property and make it stand out to guests.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {uniqueSellingPointOptions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.uniqueSellingPoints?.includes(option) ? '#4C4848' : '#333',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.uniqueSellingPoints?.includes(option)
                    ? '#F2E9DF'
                    : '#fff',
                  borderColor: formData.uniqueSellingPoints?.includes(option)
                    ? '#4C4848'
                    : '#EDEFF4',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.uniqueSellingPoints?.includes(option) || false}
                  onChange={(e) => handleUniqueSellingPointChange(option, e.target.checked)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Nearby local highlights
          </label>
          <p
            style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: '1.4',
            }}
          >
            Select what's nearby your property to help travelers understand the area :
          </p>
          {highlightsLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginRight: '12px',
                    }}
                  />
                  <div
                    style={{
                      width: '80px',
                      height: '14px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : highlightsError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              Error: {highlightsError}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {nearbyHighlights.map((highlight) => (
                <label
                  key={highlight.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: formData.nearbyHighlights?.includes(highlight.name) ? '#4C4848' : '#333',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: formData.nearbyHighlights?.includes(highlight.name)
                      ? '#F2E9DF'
                      : '#fff',
                    borderColor: formData.nearbyHighlights?.includes(highlight.name)
                      ? '#4C4848'
                      : '#EDEFF4',
                  }}
                >
                  <img
                    src={highlight.icon_url}
                    alt={highlight.name}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      objectFit: 'contain',
                      filter: formData.nearbyHighlights?.includes(highlight.name)
                        ? 'brightness(0) saturate(100%) invert(28%) sepia(0%) saturate(0%) hue-rotate(138deg) brightness(95%) contrast(88%)'
                        : 'brightness(0) saturate(100%) invert(13%) sepia(7%) saturate(1017%) hue-rotate(202deg) brightness(97%) contrast(88%)',
                    }}
                  />
                  <input
                    type="checkbox"
                    checked={formData.nearbyHighlights?.includes(highlight.name) || false}
                    onChange={(e) => handleNearbyHighlightChange(highlight.name, e.target.checked)}
                    style={{ display: 'none', accentColor: '#4C4848' }}
                  />
                  {highlight.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Noise level
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            {noiseLevelOptions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.noiseLevel === option ? '#4C4848' : '#333',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.noiseLevel === option ? '#F2E9DF' : '#fff',
                  borderColor: formData.noiseLevel === option ? '#4C4848' : '#EDEFF4',
                }}
              >
                <input
                  type="radio"
                  name="noiseLevel"
                  value={option}
                  checked={formData.noiseLevel === option}
                  onChange={(e) => handleInputChange('noiseLevel', e.target.value)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            View type
          </label>
          {viewTypesLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginRight: '12px',
                    }}
                  />
                  <div
                    style={{
                      width: '80px',
                      height: '14px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : viewTypesError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              Error: {viewTypesError}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {viewTypes.map((viewType) => (
                <label
                  key={viewType.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: formData.viewType?.includes(viewType.name) ? '#4C4848' : '#333',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: formData.viewType?.includes(viewType.name)
                      ? '#F2E9DF'
                      : '#fff',
                    borderColor: formData.viewType?.includes(viewType.name) ? '#4C4848' : '#EDEFF4',
                  }}
                >
                  <img
                    src={viewType.icon_url}
                    alt={viewType.name}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      objectFit: 'contain',
                      filter: formData.viewType?.includes(viewType.name)
                        ? 'brightness(0) saturate(100%) invert(28%) sepia(0%) saturate(0%) hue-rotate(138deg) brightness(95%) contrast(88%)'
                        : 'brightness(0) saturate(100%) invert(13%) sepia(7%) saturate(1017%) hue-rotate(202deg) brightness(97%) contrast(88%)',
                    }}
                  />
                  <input
                    type="checkbox"
                    checked={formData.viewType?.includes(viewType.name) || false}
                    onChange={(e) => handleViewTypeChange(viewType.name, e.target.checked)}
                    style={{ display: 'none', accentColor: '#4C4848' }}
                  />
                  {viewType.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Amenities
          </label>
          {amenitiesLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginRight: '12px',
                    }}
                  />
                  <div
                    style={{
                      width: '80px',
                      height: '14px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : amenitiesError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              Error: {amenitiesError}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {amenities.map((amenity) => (
                <label
                  key={amenity.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: formData.amenities?.includes(amenity.name) ? '#4C4848' : '#333',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: formData.amenities?.includes(amenity.name)
                      ? '#F2E9DF'
                      : '#fff',
                    borderColor: formData.amenities?.includes(amenity.name) ? '#4C4848' : '#EDEFF4',
                  }}
                >
                  <img
                    src={amenity.icon_url}
                    alt={amenity.name}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      objectFit: 'contain',
                      filter: formData.amenities?.includes(amenity.name)
                        ? 'brightness(0) saturate(100%) invert(28%) sepia(0%) saturate(0%) hue-rotate(138deg) brightness(95%) contrast(88%)'
                        : 'brightness(0) saturate(100%) invert(13%) sepia(7%) saturate(1017%) hue-rotate(202deg) brightness(97%) contrast(88%)',
                    }}
                  />
                  <input
                    type="checkbox"
                    checked={formData.amenities?.includes(amenity.name) || false}
                    onChange={(e) => handleAmenityChange(amenity.name, e.target.checked)}
                    style={{ display: 'none', accentColor: '#4C4848' }}
                  />
                  {amenity.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Building facilities
          </label>
          {buildingFacilitiesLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginRight: '12px',
                    }}
                  />
                  <div
                    style={{
                      width: '80px',
                      height: '14px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : buildingFacilitiesError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              Error: {buildingFacilitiesError}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {buildingFacilities.map((facility) => (
                <label
                  key={facility.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: formData.buildingFacilities?.includes(facility.name)
                      ? '#4C4848'
                      : '#333',
                    padding: '16px',
                    border: '1px solid #EDEFF4',
                    borderRadius: '8px',
                    backgroundColor: formData.buildingFacilities?.includes(facility.name)
                      ? '#F2E9DF'
                      : '#fff',
                    borderColor: formData.buildingFacilities?.includes(facility.name)
                      ? '#4C4848'
                      : '#EDEFF4',
                  }}
                >
                  <img
                    src={facility.icon_url}
                    alt={facility.name}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      objectFit: 'contain',
                      filter: formData.buildingFacilities?.includes(facility.name)
                        ? 'brightness(0) saturate(100%) invert(28%) sepia(0%) saturate(0%) hue-rotate(138deg) brightness(95%) contrast(88%)'
                        : 'brightness(0) saturate(100%) invert(13%) sepia(7%) saturate(1017%) hue-rotate(202deg) brightness(97%) contrast(88%)',
                    }}
                  />
                  <input
                    type="checkbox"
                    checked={formData.buildingFacilities?.includes(facility.name) || false}
                    onChange={(e) => handleBuildingFacilityChange(facility.name, e.target.checked)}
                    style={{ display: 'none', accentColor: '#4C4848' }}
                  />
                  {facility.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: !isNextDisabled ? '0 2px 8px rgba(76, 72, 72, 0.3)' : 'none',
            }}
            onMouseDown={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(0.95)'
              }
            }}
            onMouseUp={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            {isSubmittingDraft ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step1_2
